import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, History, Settings, LogOut, ClipboardList, PiggyBank, UsersRound, Building2, Clock } from 'lucide-react';
import { authService } from '../services/auth';
import { toast } from 'sonner';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = authService.getUser();
  const permissions = user?.permissions || [];

  // Check if user can see team management
  const canViewTeam = permissions.includes('manage_users') || permissions.includes('view_users');

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: '/suppliers', label: 'Suppliers', icon: Users, show: permissions.includes('view_suppliers') || permissions.includes('manage_suppliers') },
    { path: '/purchase-requisitions', label: 'Purchase Requisitions', icon: ClipboardList, show: permissions.includes('view_pr') || permissions.includes('create_pr') },
    { path: '/rfqs', label: 'RFQs', icon: FileText, show: permissions.includes('view_rfq') || permissions.includes('create_rfq') },
    { path: '/history', label: 'History', icon: History, show: permissions.includes('view_history') },
    { path: '/savings', label: 'Savings', icon: PiggyBank, show: permissions.includes('view_savings') },
    { path: '/tat-report', label: 'TAT Report', icon: Clock, show: permissions.includes('view_savings') || permissions.includes('view_history') },
    { path: '/team', label: 'Team', icon: UsersRound, show: canViewTeam },
    { path: '/settings', label: 'Settings', icon: Settings, show: permissions.includes('view_settings') || permissions.includes('manage_settings') },
  ].filter(item => item.show !== false);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    authService.logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <div className="app-container" data-testid="app-layout">
      <aside className="sidebar">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <img 
              src="/images/cloudrfq-logo.png" 
              alt="CloudRFQ" 
              className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-slate-900" data-testid="app-title">
                CloudRFQ
              </h1>
              <p className="text-xs text-slate-500">RFQ Management</p>
            </div>
          </div>
          {user && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs font-medium text-slate-700">{user.name}</p>
              <p className="text-xs text-slate-500">{user.email}</p>
              {user.organization_name && (
                <div className="flex items-center gap-1 mt-1">
                  <Building2 size={12} className="text-slate-400" />
                  <p className="text-xs text-slate-400">{user.organization_name}</p>
                </div>
              )}
              {user.role_names && user.role_names.length > 0 && (
                <p className="text-xs text-slate-400 mt-1">{user.role_names.join(', ')}</p>
              )}
            </div>
          )}
        </div>
        <nav className="p-4" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium text-sm">{item.label}</span>
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors duration-200 text-red-600 hover:bg-red-50 w-full mt-4"
          >
            <LogOut size={20} />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </nav>
      </aside>
      <main className="main-content" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;